/**
 * B-new-33 — post-login redirect resolution.
 *
 * Picks the safe destination for a successful sign-in / sign-up callback,
 * preferring (in order): the `?redirect=` query param → the
 * `xeito:intendedPath` sessionStorage value the dashboard guard stored when
 * it bounced an unauthenticated user → the locale-aware dashboard overview
 * fallback.
 *
 * Open-redirect guard (B-new-1129): a `/`-relative-looking string is not
 * enough. Under WHATWG URL parsing a backslash right after the leading `/`
 * (or in place of it) is normalised to `/`, and TAB/CR/LF bytes are
 * stripped from the input BEFORE parsing — so `/\evil.example`,
 * `/\t/evil.example`, etc. all satisfy a naive `startsWith('/') &&
 * !startsWith('//')` check yet resolve to a FOREIGN origin once a browser
 * (or `new URL()`) actually parses them. The guard below settles the
 * question the only way that matches what the browser will do: parse the
 * candidate against a known-safe base and compare `.origin`.
 */

export const INTENDED_PATH_STORAGE_KEY = 'xeito:intendedPath'

// Neutral fallback for non-browser contexts (this module can be imported
// under Node/SSR/tests where `window` doesn't exist) — any same-origin
// placeholder works since the check is relative-to-base, not tied to a
// real deployment host.
function defaultOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'https://app.xeito.ai'
}

export function isSafeRedirectPath(value: unknown, baseOrigin: string = defaultOrigin()): value is string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return false
  try {
    return new URL(value, baseOrigin).origin === baseOrigin
  } catch {
    return false
  }
}

export function resolveSafeRedirectPath(opts: {
  fromQuery?: string | undefined | null
  fromSessionStorage?: string | null
  fallback: string
  baseOrigin?: string
}): string {
  const baseOrigin = opts.baseOrigin ?? defaultOrigin()
  const candidate = opts.fromQuery ?? opts.fromSessionStorage ?? null
  // B-new-1008 — a bare '/' is `isSafeRedirectPath`-true (it IS a safe,
  // same-origin path) but useless as a post-login destination: it just
  // re-resolves through the root redirect instead of landing the user
  // somewhere real. Treat it the same as an unsafe candidate and fall
  // through to the fallback. Not an open-redirect concern (that guard is
  // untouched) — purely "don't bounce back to the resolver".
  if (candidate === '/') return opts.fallback
  if (!isSafeRedirectPath(candidate, baseOrigin)) return opts.fallback
  // Re-serialize the PARSED value rather than trusting the raw candidate
  // string — this is what makes the guard's verdict and the navigated
  // target the same thing. Returning the raw candidate here would let a
  // string that only *parses* safely (because URL() normalised it) diverge
  // from what a second, less careful consumer does with the original text.
  const url = new URL(candidate, baseOrigin)
  return url.pathname + url.search + url.hash
}

export function readIntendedPathFromSession(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(INTENDED_PATH_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * B-new-9220 — first signed-in landing.
 *
 * Precedence: explicit `?redirect=` > stored intent > callbackURL > fallback.
 * Every candidate must pass the open-redirect guard; an unsafe one falls
 * through to the next. A callbackURL that is only the default landing
 * (`/{l}/dashboard/overview`, or the `/{l}/welcome` landing step) is NOT an
 * explicit destination: every auth flow builds its callback from that
 * fallback, so honouring it would defeat the routing.
 *
 * Fallback: completed -> dashboard overview, not completed -> onboarding.
 * `hasCompletedOnboarding` must be the server-derived value from
 * `user.getUser`, never the raw users column.
 */
export function resolvePostAuthLandingPath(opts: {
  explicitRedirect?: string | null
  intent?: string | null
  callbackURL?: string | null
  locale: string
  hasCompletedOnboarding: boolean
  baseOrigin?: string
}): string {
  const baseOrigin = opts.baseOrigin ?? defaultOrigin()
  const dashboard = `/${opts.locale}/dashboard/overview`
  const fallback = opts.hasCompletedOnboarding ? dashboard : `/${opts.locale}/onboarding`
  const defaultCallbacks = new Set([dashboard, `/${opts.locale}/welcome`])

  const pick = (candidate: string | null | undefined, skipDefaults = false): string | null => {
    if (candidate === '/' || !isSafeRedirectPath(candidate, baseOrigin)) return null
    const url = new URL(candidate, baseOrigin)
    if (skipDefaults && defaultCallbacks.has(url.pathname)) return null
    return url.pathname + url.search + url.hash
  }

  return (
    pick(opts.explicitRedirect) ??
    pick(opts.intent) ??
    pick(opts.callbackURL, true) ??
    fallback
  )
}
